///
/// EarthBrowser
///
/// Created by Matt Giger on 11/12/2023
/// Copyright (c) 2023 Geodata Labs

import { round } from "./math"

/**
 * Represents a 2D vector with x and y components.
 */
export class vec2 {
	static zero = new vec2(0, 0)

	/**
	 * Creates a new vec2 instance.
	 * @param x The x-component of the vector.
	 * @param y The y-component of the vector.
	 */
	constructor(public readonly x: number = 0, public readonly y: number = 0) {}

	/**
	 * Returns a string representation of the vector.
	 * @returns A string in the format "(x, y)".
	 */
	public toString(): string {
		return `(${round(this.x, 2)}, ${round(this.y, 2)})`
	}

	/**
	 * Subtracts another vector from this vector.
	 * @param other The vector to subtract.
	 * @returns A new vec2 instance representing the difference.
	 */
	public subtract(other: vec2): vec2 {
		return new vec2(this.x - other.x, this.y - other.y)
	}

	/**
	 * Calculates the length (magnitude) of the vector.
	 * @returns The length of the vector.
	 */
	public get length(): number {
		return Math.sqrt(this.x*this.x + this.y*this.y)
	}
}

/**
 * Represents a 3D vector with x, y, and z components.
 */
export class vec3 {
	/** A vector with all components set to zero. */
	static zero = new vec3(0, 0, 0)
	/** A unit vector pointing in the positive x direction. */
	static unitX = new vec3(1, 0, 0)
	/** A unit vector pointing in the positive y direction. */
	static unitY = new vec3(0, 1, 0)
	/** A unit vector pointing in the positive z direction. */
	static unitZ = new vec3(0, 0, 1)
	/** A unit vector pointing in the negative x direction. */
	static negX = new vec3(-1, 0, 0)
	/** A unit vector pointing in the negative y direction. */
	static negY = new vec3(0, -1, 0)
	/** A unit vector pointing in the negative z direction. */
	static negZ = new vec3(0, 0, -1)

	/** The x-component of the vector. */
	readonly x: number
	/** The y-component of the vector. */
	readonly y: number
	/** The z-component of the vector. */
	readonly z: number

	/**
	 * Creates a new vec3 instance.
	 * @param x The x-component of the vector, or an array of three numbers.
	 * @param y The y-component of the vector.
	 * @param z The z-component of the vector.
	 */
	constructor(x: number | number[] = 0, y: number = 0, z: number = 0) {
		if(Array.isArray(x)) {
			[ x, y, z ] = x
		}
		this.x = x
		this.y = y
		this.z = z
	}

	/**
	 * Checks if the vector is a zero vector.
	 * @returns True if all components are zero, false otherwise.
	 */
	public get isZero(): boolean {
		return this.x == 0 && this.y == 0 && this.z == 0
	}

	/**
	 * Adds two vec3 vectors.
	 * @param b - The second vector
	 * @returns A new vec3 representing the sum of the input vectors
	 */
	public add(b: vec3): vec3 {
		return new vec3(this.x + b.x, this.y + b.y, this.z + b.z)
	}

	/**
	 * Subtracts one vec3 vector from another.
	 * @param b - The vector to subtract
	 * @returns A new vec3 representing the difference between the input vectors
	 */
	public subtract(b: vec3): vec3 {
		return new vec3(this.x - b.x, this.y - b.y, this.z - b.z)
	}

	/**
	 * Multiplies a vec3 vector by a scalar.
	 * @param s - The scalar value
	 * @returns A new vec3 representing the scaled vector
	 */
	public multiply(s: number): vec3 {
		return new vec3(this.x * s, this.y * s, this.z * s)
	}

	/**
	 * Returns a string representation of the vector.
	 * @returns A string in the format "{x, y, z}".
	 */
	public toString(): string {
		return `(${round(this.x, 2)}, ${round(this.y, 2)}, ${round(this.z, 2)})`
	}

	/**
	 * Calculates the length (magnitude) of the vector.
	 * @returns The length of the vector.
	 */
	public get length(): number {
		return Math.sqrt(this.x*this.x + this.y*this.y + this.z*this.z)
	}
}

/**
 * Represents a 4D vector with x, y, z, and w components.
 */
export class vec4 {
	/** The x component of the vector */
	readonly x: number
	/** The y component of the vector */
	readonly y: number
	/** The z component of the vector */
	readonly z: number
	/** The w component of the vector */
	readonly w: number

	/**
	 * Creates a new vec4 instance.
	 * @param x - The x component or an array of 4 numbers
	 * @param y - The y component (default: 0)
	 * @param z - The z component (default: 0)
	 * @param w - The w component (default: 0)
	 */
	constructor(x: number | number[] = 0, y: number = 0, z: number = 0, w: number = 0) {
		if(Array.isArray(x)) {
			[ x, y, z, w ] = x
		}
		this.x = x
		this.y = y
		this.z = z
		this.w = w
	}

	/**
	 * Returns a string representation of the vector.
	 * @returns A string in the format "{x, y, z, w}".
	 */
	public toString(): string {
		return `(${round(this.x, 2)}, ${round(this.y, 2)}, ${round(this.z, 2)}, ${round(this.w, 2)})`
	}
}

/**
 * Normalizes a vec3 vector (makes it unit length).
 * @param a - The vector to normalize
 * @returns A new vec3 representing the normalized vector
 */
export function normal(a: vec3): vec3 {
	const len = Math.sqrt(a.x*a.x + a.y*a.y + a.z*a.z)
	return new vec3(a.x / len, a.y / len, a.z / len)
}

/**
 * Calculates the dot product of two vec3 vectors.
 * @param a - The first vector
 * @param b - The second vector
 * @returns The dot product of the two vectors
 */
export function dot(a: vec3, b: vec3): number {
	return a.x*b.x + a.y*b.y + a.z*b.z
}

/**
 * Calculates the cross product of two vec3 vectors.
 * @param a - The first vector
 * @param b - The second vector
 * @returns A new vec3 representing the cross product of the input vectors
 */
export function cross(a: vec3, b: vec3): vec3 {
	return new vec3(a.y*b.z-a.z*b.y, a.z*b.x-a.x*b.z, a.x*b.y-a.y*b.x)
}

/**
 * Calculates the outer product of two vec3 vectors.
 * @param a - The first vector
 * @param b - The second vector
 * @returns A new vec3 representing the outer product of the input vectors
 */
export function outer(a: vec3, b: vec3): vec3 {
	return new vec3(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x)
}

/**
 * Linearly interpolates between two vec3 vectors.
 * @param a - The first vector
 * @param b - The second vector
 * @param progress - The interpolation progress (0-1)
 * @returns A new vec3 representing the interpolated vector
 */
export function vec3lerp(a: vec3, b: vec3, progress: number): vec3 {
	return new vec3(
		a.x + (b.x - a.x) * progress,
		a.y + (b.y - a.y) * progress,
		a.z + (b.z - a.z) * progress)
}

/**
 * Spherically interpolates between two vec3 vectors.
 * This function interpolates along the surface of a sphere, maintaining constant angular velocity.
 * @param a - The first vector (should be normalized for best results)
 * @param b - The second vector (should be normalized for best results)
 * @param progress - The interpolation progress (0-1)
 * @returns A new vec3 representing the interpolated vector
 */
export function slerp(a: vec3, b: vec3, progress: number): vec3 {
	// Handle edge cases
	if (progress <= 0) return a
	if (progress >= 1) return b

	// Calculate the dot product to determine the angle between vectors
	const dotProduct = dot(a, b)

	// If the dot product is negative, the vectors are pointing in opposite directions
	// We need to flip one of them to take the shorter path
	let bAdjusted = b
	if (dotProduct < 0) {
		bAdjusted = new vec3(-b.x, -b.y, -b.z)
	}

	// Calculate the angle between the vectors
	const angle = Math.acos(Math.abs(dotProduct))

	// If the angle is very small, use linear interpolation to avoid numerical issues
	const epsilon = 1e-6
	if (angle < epsilon) {
		return vec3lerp(a, bAdjusted, progress)
	}

	// Calculate the interpolation coefficients
	const sinAngle = Math.sin(angle)
	const coeffA = Math.sin((1 - progress) * angle) / sinAngle
	const coeffB = Math.sin(progress * angle) / sinAngle

	// Return the interpolated vector
	return new vec3(
		coeffA * a.x + coeffB * bAdjusted.x,
		coeffA * a.y + coeffB * bAdjusted.y,
		coeffA * a.z + coeffB * bAdjusted.z
	)
}